import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styles from './FullCardPreview.module.css';

interface Props {
  /** Whether the modal is open. Renders nothing when false. */
  isOpen: boolean;
  /** Already-built card PNG as a data URL. Renders nothing when null. */
  dataUrl: string | null;
  /**
   * Layout pixel width of the captured card (Ship 2a.3). Plumbed
   * to the `<img>`'s `width` attr so the browser reserves layout
   * space before the data URL decodes (no decode-time shift).
   * Null while no asset is ready.
   */
  cardWidth: number | null;
  /**
   * Layout pixel height of the captured card (Ship 2a.3). Variable
   * per card (grows past 2400 when verse + prose stack tall).
   * Plumbed to the `<img>`'s `height` attr for the same decode-shift
   * mitigation. Null while no asset is ready.
   */
  cardHeight: number | null;
  /** Accessible label for the dialog (typically the sheet's recipient-aware title). */
  ariaLabel: string;
  /** Called when the modal should close (backdrop tap, close button, etc.). */
  onClose: () => void;
  /** Coordinator z for the `full-card-preview` layer (A1), applied to the
   *  portal root. CSS z (250) is the fallback when undefined. */
  zIndex?: number;
}

/**
 * Tap-to-expand modal for the prayer share card (Ship 2a.1, D7).
 * The in-sheet preview thumbnail is small per LetThemKnowSheet's
 * chrome budget; this modal lets the user see the actual card at
 * near-viewport size to verify content before sending.
 *
 * Sizing is width-first per codex pass-7 BLOCKER 1: `min(95vw, 480px)`
 * with vertical scroll so the card text scales by viewport WIDTH (not
 * fits the card by height). On iPhone SE (375px wide), the modal
 * card lands at ~356px wide; effective text sizes hit the readable
 * floor (proof 25px, verseLg 29px, cardLabel 18.5px, prose at same
 * size as verse). Card height = 791px exceeds the viewport, so the
 * scrollable wrapper lets the user pan vertically.
 *
 * Escape handling lives in the PARENT (LetThemKnowSheet's state-gated
 * window listener). This component does NOT register its own Escape
 * listener — that would race the parent's listener and could close the
 * sheet itself. Backdrop tap + close button click both route through
 * `onClose`, which the parent wires to `setPreviewExpanded(false)`.
 *
 * Focus management: on open, focus moves to the close button; on close,
 * focus returns to the trigger button (the parent owns the trigger ref
 * and restores focus via the `isOpen` transition).
 */
export default function FullCardPreview({
  isOpen,
  dataUrl,
  cardWidth,
  cardHeight,
  ariaLabel,
  onClose,
  zIndex,
}: Props) {
  const { t } = useTranslation();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  // Focus the close button when the modal opens so keyboard users land
  // on a meaningful target. Parent restores focus to the trigger on
  // close via its own `isOpen` transition handling.
  useEffect(() => {
    if (!isOpen) return;
    const raf = requestAnimationFrame(() => {
      closeBtnRef.current?.focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  // Lock body scroll while the modal is open so the underlying sheet
  // can't scroll underneath the backdrop.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !dataUrl) return null;

  return createPortal(
    <div
      className={styles.root}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
    >
      <button
        type="button"
        className={styles.backdrop}
        aria-label={t('nav.cancel')}
        onClick={onClose}
        tabIndex={-1}
      />
      <div className={styles.scroll}>
        <img
          src={dataUrl}
          alt={t('share.imageAlt')}
          className={styles.card}
          {...(cardWidth && cardHeight
            ? { width: cardWidth, height: cardHeight }
            : {})}
        />
      </div>
      <button
        ref={closeBtnRef}
        type="button"
        className={styles.close}
        aria-label={t('nav.cancel')}
        onClick={onClose}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>,
    document.body,
  );
}
